import React, { useState } from "react";
import { getIcon, INTEGRATION_ENUM } from "../../constants/constants";
import LoadingComponent from "../../components/common/LoadingComponent";
import { formatIntegrationName } from "../../constants/constants";
import MultiSelect from "../../components/common/MultiSelect";
import { getIntegrationConfig } from "./Integrations.utils";
import { INTEGRATION_OPTIONS } from "./Integrations.utils";
import { OptionList } from "../OAuth/WorkspaceProject.utils";
import { DEFAULT_SETTINGS_STEP } from "../OAuth/PlatformWorkspace.utils";

function Configuration({
  setting,
  configurations,
  loading,
  selectedDefaultSettings,
  setSelectedDefaultSettings,
  handleSave,
  isOnboarding,
  isUpdateMode,
  selectedTeam,
  handleTeamChange,
  selectedPage,
  handlePageChange,
}) {
  const [currentStep, setCurrentStep] = useState(0);

  const rawIntegrationName = setting?.integrationId?.name;
  const integrationName = rawIntegrationName?.toLowerCase();
  const integrationCfg = INTEGRATION_OPTIONS[integrationName] || {
    preSteps: [],
  };

  const { preSteps, hasDefaultSettings = true } = integrationCfg;
  const allSteps = hasDefaultSettings
    ? [...preSteps, DEFAULT_SETTINGS_STEP]
    : [...preSteps];
  const iconSrc = getIcon(rawIntegrationName);
  const config = getIntegrationConfig(rawIntegrationName);
  const isInstagram = integrationName?.includes("instagram");

  const currentStepKey = allSteps[currentStep]?.key;

  const stepHeading =
    integrationName === INTEGRATION_ENUM.TWILIO
      ? "Select Twilio Number"
      : currentStepKey === "page"
      ? isInstagram
        ? "Select Instagram Page"
        : "Select Facebook Page"
      : `Select ${formatIntegrationName(rawIntegrationName)} Default Settings`;

  const fieldData = configurations?.fieldData || {};

  const isLastStep = currentStep === allSteps.length - 1;
  const canGoBack = currentStep > 0;

  const onTeamSelect = (id) => {
    handleTeamChange(id);
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const handleNext = async () => {
    if (isLastStep) {
      handleSave();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const isNextDisabled = (() => {
    switch (currentStepKey) {
      case "team":
        return !selectedTeam;
      case "page":
        return !selectedPage;
      case "defaultSettings": {
        return (
          !selectedDefaultSettings ||
          Object.values(selectedDefaultSettings).every(
            (val) => !val || (Array.isArray(val) && val.length === 0),
          )
        );
      }
      default:
        return false;
    }
  })();

  const renderStepContent = () => {
    if (loading) {
      return <LoadingComponent loading={true} />;
    }

    switch (currentStepKey) {
      case "page": {
        const pages = fieldData?.pages || [];

        const pageOptions = pages.map(({ key, value }) => ({
          id: key,
          label: value,
        }));

        return (
          <OptionList
            options={pageOptions}
            selectedId={selectedPage}
            onSelect={(id) => handlePageChange && handlePageChange(id)}
            emptyMessage={
              isInstagram
                ? "No Facebook Pages with connected Instagram Professional account found."
                : "No Facebook Pages found."
            }
          />
        );
      }

      case "team": {
        const teams = fieldData?.teams || [];

        const teamsOptions = teams.map(({ key, value }) => ({
          id: key,
          label: value,
        }));

        return (
          <OptionList
            options={teamsOptions}
            selectedId={selectedTeam}
            onSelect={isUpdateMode ? undefined : onTeamSelect}
            emptyMessage="No teams found."
            disabled={isUpdateMode}
          />
        );
      }

      case "defaultSettings": {
        const resources = fieldData?.resources || [];

        if (!resources.length) {
          return (
            <p
              className="text-center py-3 mb-0"
              style={{
                color: "rgba(255,255,255,0.3)",
                fontSize: 14,
              }}
            >
              No configurable default settings available.
            </p>
          );
        }

        return (
          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-label ws-label">
                Phone Numbers
                <span className="text-danger fs-9">*</span>
              </label>

              <MultiSelect
                options={resources.map(({ key, value }) => ({
                  value: key,
                  label: value,
                }))}
                value={
                  selectedDefaultSettings?.resources?.map(({ key }) => key) ||
                  []
                }
                onChange={(selectedKeys) => {
                  const selectedOptions = resources.filter(({ key }) =>
                    selectedKeys.includes(key),
                  );

                  setSelectedDefaultSettings((prev) => ({
                    ...prev,
                    resources: selectedOptions,
                  }));
                }}
              />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className={`container-fluid mt-10 ${!isOnboarding ? "px-4" : ""}`}
      style={
        isOnboarding ? { paddingLeft: "20rem", paddingRight: "20rem" } : {}
      }
    >
      <div className="card">
        <div className="ws-card-header px-4 py-3 d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-body-emphasis"
            style={{ width: 50, height: 50 }}
          >
            <img
              alt={integrationName}
              src={iconSrc}
              style={{ width: 22, height: 22 }}
            />
          </div>

          <div>
            <p className="ws-step-heading mb-1">Integration Setup</p>
            <h5 className="mb-0 fw-bold">{config?.title}</h5>
          </div>
        </div>

        {(setting?.maskedAccessToken || setting?.sessionId) && (
          <div className="px-4 py-4 ws-panel">
            <p className="ws-label mb-2">{stepHeading}</p>
            {renderStepContent()}
          </div>
        )}

        <div className="ws-card-footer px-4 py-3 d-flex justify-content-end gap-2">
          {canGoBack && (
            <button className="btn btn-outline-secondary" onClick={handleBack}>
              ← Back
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={isNextDisabled}
            onClick={handleNext}
          >
            {isLastStep ? "Save & Continue" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Configuration;
